import { v7 as uuid7 } from 'uuid';
import React, { useEffect, useState } from 'react';
import {
  useNavigate,
  useLocation,
  useBlocker,
  BlockerFunction
} from 'react-router-dom';

import { Spinner } from '../components/spinner';
import { PlayerStateFull } from '../types/state';
import { getFromStore, putToStore } from '../utils/indexedDB';
import { NavigateToWorldMap } from '../utils/game-engine/player';
import { UpdatePlayerStateAsync } from '../utils/game-engine/base';
import { PlayerAssessmentState } from '../types/state/player/player-assessment';
import { usePlayerNavigationGuard } from '../hooks/user-player-navigation-guard';
import { isNetworkOnline } from '../utils/indicator';
import { postPlayerState } from '../services/syncronize-state';
import { mergeServerPlayerState } from '../utils/game-engine/player-merge';

const PlayerContext = React.createContext<
  [
    initialState: PlayerContextState,
    setState: React.Dispatch<React.SetStateAction<PlayerContextState>>
  ]
>([{}, () => {}]);

interface PlayerStateProviderProps {
  gameCode: string;
  children: React.ReactNode;
}

const PlayerStateProvider = ({
  gameCode,
  ...props
}: PlayerStateProviderProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [state, setState] = useState<PlayerContextState>({
    gameCode
  });

  usePlayerNavigationGuard(state);

  const initPlayerLocation = () => {
    if (state.playerState?.location) return;

    setState((prev) => {
      const response = NavigateToWorldMap(prev);
      return UpdatePlayerStateAsync(prev, response);
    });
  };

  useEffect(() => {
    const playerCode = state.playerState?.code;
    const playerStatus = state.playerState?.status;

    if (!playerCode || playerStatus === 'waiting') return;
    initPlayerLocation();
  }, [state.playerState?.code, state.playerState?.status]);

  useEffect(() => {
    const loadPlayerState = async () => {
      const player = await getFromStore<PlayerContextState>(
        'player-state',
        gameCode
      );

      let navigateTo = location.pathname.includes('preload')
        ? `/preload/${gameCode}`
        : `/games/${gameCode}/`;

      if (!player) {
        const content = {
          gameCode,
          playerState: BuildNewPlayerState()
        };

        setState(content);
        putToStore('player-state', content);
        putToStore('upload-queue', {
          playerCode: content.playerState.code,
          files: []
        });
        putToStore('scoring-queue', {
          playerCode: content.playerState.code,
          scorings: []
        });

        navigate(navigateTo);
      } else {
        if (!location.pathname.includes('assessment')) {
          setState(player);

          if (player.playerState?.code && isNetworkOnline()) {
            postPlayerState(
              gameCode,
              player.playerState.code,
              JSON.stringify(player)
            )
              .then((response) => {
                if (response) {
                  setState((prev) => mergeServerPlayerState(prev, response));
                }
              })
              .catch((err) => {
                console.error('Failed to sync player state on load', err);
              });
          }

          if (player.playerState?.name && player.playerState?.avatarImage) {
            if (player.playerState.status === 'ready') {
              navigate(`/games/${gameCode}/lobby`, { replace: true });
            }
            if (player.playerState.status === 'playing') {
              navigate(`/games/${gameCode}/map`, { replace: true });
            }
          } else {
            navigate(navigateTo, { replace: true });
          }
        }
      }
    };

    loadPlayerState();
  }, []);

  return (
    <PlayerContext.Provider value={[state, setState]}>
      {state.playerState ? props.children : <Spinner />}
    </PlayerContext.Provider>
  );
};

export interface PlayerContextState {
  gameCode?: string;
  playerState?: PlayerStateFull;
  assessmentState?: PlayerAssessmentState;
}

export const BuildNewPlayerState = (): PlayerStateFull => {
  return {
    id: undefined,
    name: '',
    language: '',
    avatarImage: '',
    status: 'waiting',
    score: 0,
    coordinates: {
      latitude: 0,
      longitude: 0,
      accuracy: 0,
      altitude: 0,
      heading: 0
    },
    displayLanguageUrl: '',
    heading: 0,
    location: '',
    code: uuid7(),
    teamCode: '',
    inventory: [],
    titles: [],
    timers: [],
    zones: [],
    areas: [],
    tasks: [],
    latestCompletedTaskId: '',
    openedOnDemandTask: '',
    isPreloadCompleted: false
  };
};

export { PlayerContext, PlayerStateProvider };
